import React from "react";
import { useSnackbar } from "../../ui-components/snackbarProvider";

// material import
import { Box, Button, Card, Chip, Grid2, Typography } from "@mui/material";
import { formatDateForDisplay } from "../../utils/dateUtils";

// project import
import AdminDBServices from "../../services/v1/adminDBServices";
import ContentCopyTwoToneIcon from "@mui/icons-material/ContentCopyTwoTone";

const DonorDetails = ({
  selectedRow,
  fetchAllDonations,
  fetchAllInactiveDonors,
  handleCloseDialog,
}) => {
  const { showSnackbar } = useSnackbar();

  const handleDonorStatusChange = async () => {
    try {
      const response = await AdminDBServices.changeDonorStatus(
        selectedRow.donorID
      );

      console.log("API Response:", response);
      const message =
        response.data?.message ||
        response.message ||
        "Donor status changed successfully.";

      showSnackbar(message, {
        severity: "success",
      });
      fetchAllDonations();
      fetchAllInactiveDonors();
      handleCloseDialog();
    } catch (error) {
      console.error("Error changing donor status:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to change donor status.";
      showSnackbar(errorMessage, {
        severity: "error",
      });
    }
  };

  const handleCopyURL = (date) => {
    const rowData = selectedRow;

    if (!rowData) {
      console.log("Row data:", rowData);
      showSnackbar("No donor data available", { severity: "error" });
      return;
    }

    const payload = {
      reservation_number: rowData.reservationNumber,
      fullname: rowData.donorName,
      package_title: rowData.package,
      date: date,
      package_price: rowData.packageAmount,
      payment_type: rowData.paymentMethod,
      payment_option: rowData.paymentOption,
      donor_email: rowData.email,
      donorID: rowData.donorID,
    };

    const jsonData = JSON.stringify(payload);

    const base64Encoded = btoa(jsonData)
      .replace(/\+/g, "-") // Replace '+' with '-'
      .replace(/\//g, "_") // Replace '/' with '_'
      .replace(/=+$/, ""); // Remove trailing '=' padding

    const encryptedURL = `http://booking.suwaarana.org/order-pay?data=${base64Encoded}`;

    navigator.clipboard.writeText(encryptedURL);

    showSnackbar("Payment URL has been copied", {
      severity: "success",
    });
  };

  return (
    <Box sx={{ border: 1, borderRadius: 2, borderColor: "lightgray" }}>
      <Grid2 item size={{ xs: 12 }} sx={{ my: 1 }}>
        <Grid2 container spacing={1} p={3}>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Donor Name
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">
              {selectedRow.title} {selectedRow.donorName}
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Email
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{selectedRow.email}</Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              City
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{selectedRow.city}</Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Country
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{selectedRow.country}</Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Type
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{selectedRow.type || "-"}</Typography>
          </Grid2>

          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Frequency
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{selectedRow.frequency}</Typography>
          </Grid2>

          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Purpose for Sponsoring
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{selectedRow.message}</Typography>
          </Grid2>
          {selectedRow.typeID === 1 && (
            <>
              <Grid2 item size={{ xs: 5.8 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Reserved Dates :
                </Typography>
              </Grid2>
              <Grid2 item size={{ xs: 5.8 }}>
                {selectedRow.reservedDates.length > 0 ? (
                  selectedRow.reservedDates.map((date, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={2}>
                      <Typography variant="body2" mt={1}>
                        {formatDateForDisplay(date, 'YYYY-MM-DD')}
                      </Typography>
                      <Chip
                        label={
                          selectedRow.paymentStatus[index] === "Paid"
                            ? "Paid"
                            : "Pending"
                        }
                        color={
                          selectedRow.paymentStatus[index] === "Paid"
                            ? "success"
                            : "warning"
                        }
                        size="small"
                      />
                      {selectedRow.paymentStatus[index] !== "Paid" && (
                        <ContentCopyTwoToneIcon
                          color="secondary"
                          fontSize="small"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleCopyURL(date)}
                        />
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2">No Recurring Dates</Typography>
                )}
              </Grid2>
            </>
          )}
        </Grid2>
      </Grid2>
      <Grid2
        item
        size={{ xs: 12 }}
        container
        justifyContent="flex-end"
        spacing={1}
        mb={2}
        mr={2}
      >
        <Grid2 item size={{ xs: 3.5 }}>
          <Button
            size="small"
            fullWidth
            variant="outlined"
            color="secondary"
            onClick={handleDonorStatusChange}
          >
            Deactivate Donor
          </Button>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default DonorDetails;
